// Import controller interfaces
import {
    IProductController
} from '../Interfaces/ControllerInterfacesBarrel';
// Import api models.
import ApiController from './ApiController';
import {
    DisqualifiersText,
    ErrorMessage,
	PossibleBuildingUse,
	PossibleConstructionType,
	PossibleFoundationType,
	PossibleOccupancyType,
	PossibleParkingType,
	PossiblePeril,
	ValidNewBusinessInceptionDateBoundaries,
} from '@app/models/api';
// Import enumerations.
import {
    BroadLineOfBusiness,
    Peril,
} from '@app/enumerations';

export default class ProductionProductController extends ApiController implements IProductController {

    private readonly controllerUrl: string = 'v2/product';

    public constructor() {
        super();
    }

    public async getValidNewBusinessInceptionDateBoundaries(): Promise<ValidNewBusinessInceptionDateBoundaries> {
        let url: string = `${this.controllerUrl}/inception-date/valid-date-bounds`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to get inception date boundaries.';
            }
            throw error;
        }
    }

    public async getPossiblePerilsAndDeductibles(broadLineOfBusinessCode: BroadLineOfBusiness, state: string): Promise<PossiblePeril[]> {

        let url: string = `${this.controllerUrl}/possible-perils-deductibles/${broadLineOfBusinessCode}/${state}`;

        try {
            let response: Response = await super.makeGetCall(url, undefined);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to get possible perils and their deductibles.';
            }
            throw error;
        }
    }

    public async getPossibleBuildingUses(broadLineOfBusinessCode: BroadLineOfBusiness): Promise<PossibleBuildingUse[]> {
        let url: string = `${this.controllerUrl}/buildingUse/${broadLineOfBusinessCode}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to get the available building types.';
            }
            throw error;
        }
    }

    public async getPossibleOccupancyTypes(broadLineOfBusinessCode: BroadLineOfBusiness): Promise<PossibleOccupancyType[]> {
        let url: string = `${this.controllerUrl}/buildingOccupancy/${broadLineOfBusinessCode}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to get the available occupancy types.';
            }
            throw error;
        }
    }

    public async getPossibleParkingTypes(broadLineOfBusinessCode: BroadLineOfBusiness): Promise<PossibleParkingType[]> {
        let url: string = `${this.controllerUrl}/buildingParkingType/${broadLineOfBusinessCode}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to get the available parking types.';
            }
            throw error;
        }
    }

    public async getPossibleFoundationTypes(broadLineOfBusinessCode: BroadLineOfBusiness): Promise<PossibleFoundationType[]> {
        let url: string = `${this.controllerUrl}/buildingFoundation/${broadLineOfBusinessCode}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to get the available foundation types.';
            }
            throw error;
        }
    }

    public async getPossibleConstructionTypes(broadLineOfBusinessCode: BroadLineOfBusiness): Promise<PossibleConstructionType[]> {
        let url: string = `${this.controllerUrl}/constructionType/${broadLineOfBusinessCode}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to get the available construction types.';
            }
            throw error;
        }
    }

    public async getDisqualifiersText(perilCodes: Peril[], broadLineOfBusinessCode: BroadLineOfBusiness, version: number | undefined): Promise<DisqualifiersText> {
        let perilCodeArguments: string[] = perilCodes.map((perilCode: Peril) => `perilCodes=${perilCode}`);
        
        const url : string =  version ? 
            `${this.controllerUrl}/disqualifiers?${perilCodeArguments.join("&")}&broadLineOfBusiness=${broadLineOfBusinessCode}&version=${version}`
            : `${this.controllerUrl}/disqualifiers?${perilCodeArguments.join("&")}&broadLineOfBusiness=${broadLineOfBusinessCode}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to get the disqualifying text.';
            }
            throw error;
        }
    }
};